import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  MenuItem,
  Alert,
  Grid,
  Fade
} from '@mui/material';
import {
  LocationOn,
  Phone,
} from '@mui/icons-material';

const Contact: React.FC = () => {
  // SEO Meta Tags
  useEffect(() => {
    document.title = "İletişim - Kale Treyler Frigo | Mersin Frigo Dorse Tamir Servisi";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Kale Treyler Frigo ile iletişime geçin. Mersin MIG Tır Parkı'nda bulunan frigo dorse tamir servisimiz. Telefon: +90 545 288 72 93");
    }
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', "Kale Treyler iletişim, frigo dorse tamiri Mersin, MIG Tır Parkı, telefon, adres");
    }
    
    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', "İletişim - Kale Treyler Frigo | Mersin Frigo Dorse Tamir Servisi");
    }
    
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', "Kale Treyler Frigo ile iletişime geçin. Mersin MIG Tır Parkı'nda bulunan frigo dorse tamir servisimiz. Telefon: +90 545 288 72 93");
    }

    // Update Canonical URL
    const canonicalLink = (document.querySelector('link[rel="canonical"]') || document.createElement('link')) as HTMLLinkElement;
    canonicalLink.setAttribute('rel', 'canonical');
    canonicalLink.setAttribute('href', 'https://kaletreyler.com/iletisim');
    if (!document.head.contains(canonicalLink)) {
      document.head.appendChild(canonicalLink);
    }
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [showSuccess, setShowSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form data:', formData);
    setShowSuccess(true);
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: ''
    });
    setTimeout(() => setShowSuccess(false), 5000);
  };

  const contactInfo = [
    {
      title: "İletişim",
      content: "📞 +90 545 288 72 93\n📧 kaletreyler@gmail.com\n🕒 Pazartesi - Cuma: 08:00 - 18:00\n🕒 Cumartesi: 09:00 - 16:00",
      icon: <Phone sx={{ fontSize: 24, color: 'primary.main' }} />
    },
    {
      title: "Adres",
      content: "📍 Dikilitaş Mah. Cumhuriyet-5 Cad. No: 118/B\n📍 MIG Mersin Tır Parkı İçerisinde\n📍 Akdeniz / Mersin",
      icon: <LocationOn sx={{ fontSize: 24, color: 'primary.main' }} />
    }
  ];

  const subjects = [
    { value: 'arac-ici-yalitim', label: 'Araç İçi Yalıtım' },
    { value: 'double-deck', label: 'Double Deck Sistem' },
    { value: 'yedek-parca', label: 'Yedek Parça' },
    { value: 'genel-tamir', label: 'Genel Tamir Hizmetleri' },
    { value: 'diger', label: 'Diğer' },
  ];

  return (
   <Box>

      {/* Contact Form & Info */}
      <Container maxWidth="xl" sx={{ py: 8, px: { xs: 2, md: 4, lg: 6 } }}>
        <Grid container spacing={{ xs: 4, md: 6, lg: 8 }}>
          {/* Contact Form */}
          <Grid item xs={12} lg={8}>
            <Fade in timeout={1000}>
              <Box>
                <Typography variant="h3" component="h2" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
                  Mesaj Gönderin
                </Typography>
                {showSuccess && (
                  <Alert severity="success" sx={{ mb: 3 }}>
                    Mesajınız gönderildi! En kısa sürede size dönüş yapacağız.
                  </Alert>
                )}
                <Card elevation={3} sx={{ p: 4, borderRadius: 3, bgcolor: 'white' }}>
                  <form onSubmit={handleSubmit}>
                    <Grid container spacing={{ xs: 3, md: 4, lg: 5 }}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Ad Soyad"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="E-posta"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Telefon"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          select
                          label="Konu"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          required
                        >
                          {subjects.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={6}
                          label="Mesajınız"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          fullWidth
                          sx={{
                            py: 1.5,
                            fontSize: '1.1rem',
                            fontWeight: 'bold',
                            borderRadius: 2,
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: '0 8px 25px rgba(25, 118, 210, 0.3)'
                            },
                            transition: 'all 0.3s ease'
                          }}
                        >
                          Mesaj Gönder
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                </Card>
              </Box>
            </Fade>
          </Grid>

          {/* Contact Info */}
          <Grid item xs={12} lg={4}>
            <Fade in timeout={1000}>
              <Box>
                <Typography variant="h3" component="h2" gutterBottom color="primary" sx={{ fontWeight: 'bold', mb: 2 }}>
                  İletişim Bilgileri
                </Typography>
                <Grid container spacing={{ xs: 1, md: 1.5, lg: 2 }}>
                  {contactInfo.map((info, index) => (
                    <Grid item xs={12} key={index}>
                      <Fade in timeout={1000 + index * 200}>
                        <Card 
                          elevation={2} 
                          sx={{ 
                            borderRadius: 2,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: '0 8px 25px rgba(30, 58, 138, 0.15)'
                            }
                          }}
                        >
                          <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 1.5 }}>
                            <Box 
                              sx={{ 
                                p: 0.5, 
                                borderRadius: '50%', 
                                bgcolor: 'primary.50',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mt: 0.5
                              }}
                            >
                              {info.icon}
                            </Box>
                            <Box>
                              <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', mb: 0.25, fontSize: '1rem' }}>
                                {info.title}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line', fontSize: '0.8rem' }}>
                                {info.content}
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </Fade>
                    </Grid>
                  ))}
                </Grid>

                {/* Google Maps */}
                <Box mt={2}>
                  <Typography variant="h5" component="h3" gutterBottom sx={{ mb: 2 }}>
                    Konum
                  </Typography>
                  <Card
                    elevation={2}
                    sx={{
                      borderRadius: 2,
                      overflow: 'hidden'
                    }}
                  >
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d20891.422211112407!2d34.67934043181338!3d36.84637108869229!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1527f1a275aa19b5%3A0x21a6d99b8a1bb736!2sMERS%C4%B0N%20KALE%20TREYLER%20FR%C4%B0GO!5e0!3m2!1str!2str!4v1760931415499!5m2!1str!2str"
                      width="100%"
                      height="250"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Kale Treyler Frigo Konumu"
                    />
                  </Card>
                </Box>
              </Box>
            </Fade>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Contact;